import { useState, useRef } from "react";
import { API } from "@/App";
import adminApi from "../pages/admin/adminApi";
import { Button } from "@/components/ui/button";
import { Upload, X, ImageIcon } from "lucide-react";
import { toast } from "sonner";

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const MAX_SIZE = 10 * 1024 * 1024;

const ImageUploader = ({ currentUrl, onUploaded }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentUrl || "");
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error("Use JPG, PNG, WebP, or GIF images.");
      return;
    }
    if (file.size > MAX_SIZE) {
      toast.error("File too large. Max 10 MB.");
      return;
    }

    setPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await adminApi.post(`${API}/upload-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = res.data.image_url;
      setPreview(url);
      onUploaded(url);
      toast.success("Image uploaded!");
    } catch {
      toast.error("Upload failed.");
      setPreview(currentUrl || "");
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview("");
    onUploaded("");
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="space-y-2">
      {preview ? (
        <div className="relative inline-block">
          <img
            src={preview}
            alt="Preview"
            className="w-full max-h-48 object-cover rounded-lg border"
          />
          <button
            type="button"
            onClick={handleRemove}
            className="absolute top-1 right-1 bg-white/80 rounded-full p-1 hover:bg-white"
          >
            <X className="w-4 h-4 text-red-500" />
          </button>
        </div>
      ) : (
        <div
          onClick={() => inputRef.current?.click()}
          className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-[#2D6A4F] transition-colors"
          data-testid="image-upload-area"
        >
          <ImageIcon className="w-8 h-8 mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-500">Click to upload image</p>
          <p className="text-xs text-gray-400">JPG, PNG, WebP, GIF (max 10MB)</p>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFile}
        className="hidden"
        data-testid="image-file-input"
      />
      {!preview && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="upload-image-btn"
        >
          <Upload className="w-4 h-4 mr-1" />
          {uploading ? "Uploading..." : "Choose Image"}
        </Button>
      )}
    </div>
  );
};

export default ImageUploader;
